import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, OnChanges, SimpleChanges,
  HostBinding,
  inject
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { FormSchema } from '../../models/form-schema.model';
import { FormSubmitEvent, FormChangeEvent } from '../../models/form-output.model';
import { AdminFormBuilderService }  from '../../services/form-builder.service';
import { FormStateService }         from '../../services/form-state.service';
import { ConditionEngineService }   from '../../services/condition-engine.service';
import { DynamicFieldComponent }    from '../dynamic-field/dynamic-field.component';

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DynamicFieldComponent],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit, OnDestroy, OnChanges {

  @Input() schema!: FormSchema;
  @Input() data?: Record<string, unknown>;
  @Input() disabled = false;

  @Output() formSubmit = new EventEmitter<FormSubmitEvent>();
  @Output() formChange = new EventEmitter<FormChangeEvent>();
  @Output() formCancel = new EventEmitter<void>();

  private readonly builder         = inject(AdminFormBuilderService);
  private readonly conditionEngine = inject(ConditionEngineService);
  private readonly formState       = inject(FormStateService);

  form!: FormGroup;
  private destroy$ = new Subject<void>();

  // aplicar themeOverride como estilos del host
  @HostBinding('style')
  get hostStyles(): Record<string, string> {
    return this.schema?.themeOverride ?? {};
  }

  constructor() {}

  ngOnInit(): void { this.buildForm(); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.form) {
      this.builder.patchValues(this.form, this.data ?? {});
    }
    if (changes['disabled'] && this.form) {
      this.disabled
        ? this.form.disable({ emitEvent: false })
        : this.form.enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    this.form = this.builder.buildForm(this.schema, this.data);

    if (this.disabled) this.form.disable({ emitEvent: false });

    this.conditionEngine.apply(this.schema, this.form, this.destroy$);
    this.formState.track(this.schema, this.form);

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formChange.emit({
          formId:  this.schema.id,
          value:   this.form.getRawValue(),
          valid:   this.form.valid,
          dirty:   this.form.dirty,
          touched: this.form.touched,
        });
      });
  }

  onSubmit(): void {
    if (this.form.pending) return;   // esperar async validators
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.formSubmit.emit({
      formId:    this.schema.id,
      value:     this.resolveSubmitValues(this.form.getRawValue()),
      timestamp: new Date(),
    });
  }

  /**
   * Transforma los valores crudos del formulario aplicando la propiedad `saveAs`
   * de cada campo. Si `saveAs === 'label'`, sustituye el valor guardado (value)
   * por la etiqueta visible (label) de la opción seleccionada.
   * Para campos sin `saveAs` (o `saveAs === 'value'`) no se hace ningún cambio.
   */
  private resolveSubmitValues(raw: Record<string, unknown>): Record<string, unknown> {
    const result = { ...raw };
    const allFields = this.schema.sections.flatMap(s => s.fields);
    for (const field of allFields) {
      if (field.saveAs !== 'label' || !field.options?.length) continue;
      const fieldValue = result[field.id];
      if (Array.isArray(fieldValue)) {
        result[field.id] = fieldValue.map(v => {
          const opt = field.options!.find(o => String(o.value) === String(v));
          return opt ? opt.label : v;
        });
      } else if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
        const opt = field.options!.find(o => String(o.value) === String(fieldValue));
        result[field.id] = opt ? opt.label : fieldValue;
      }
    }
    return result;
  }

  onCancel(): void {
    this.builder.resetForm(this.form, this.schema);
    this.formCancel.emit();
  }

  // — API pública para ViewChild —
  reset(): void {
    this.builder.resetForm(this.form, this.schema);
  }

  patch(data: Record<string, unknown>): void {
    this.builder.patchValues(this.form, data);
  }

  getValue(): Record<string, unknown> {
    return this.form.getRawValue();
  }

  markAllTouched(): void {
    this.form.markAllAsTouched();
  }

  isFieldHidden(fieldId: string): boolean {
    return this.conditionEngine.getState(fieldId).hidden;
  }

  get isValid():   boolean { return this.form?.valid   ?? false; }
  get isDirty():   boolean { return this.form?.dirty   ?? false; }
  get isPending(): boolean { return this.form?.pending ?? false; }
}